import React from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "antd";
import Service from "../../service";

// Muted, deliberately low-chroma tints — the avatar should identify a person at a
// glance without competing with the status pills or the primary action colour.
const AVATAR_TONES = [
  { bg: "#e8eef7", fg: "#2c5282" },
  { bg: "#e9f3ec", fg: "#2f6b48" },
  { bg: "#f6ecec", fg: "#8c3b3b" },
  { bg: "#efeaf7", fg: "#5b4599" },
  { bg: "#fdf0e3", fg: "#96560d" },
  { bg: "#e6f1f3", fg: "#276a75" },
];

/** Stable tone per person, so an employee keeps the same colour across pages. */
const toneFor = (seed) => {
  const key = String(seed || "");
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) % 997;
  }
  return AVATAR_TONES[hash % AVATAR_TONES.length];
};

/**
 * First letter of the first and last word, skipping the honorific the HRMS
 * stores as part of full_name ("Mr. Bhavesh Chunch" → BC, not MB).
 */
const initialsFor = (name) => {
  const words = String(name || "")
    .replace(/[^A-Za-z\s.]/g, " ")
    .split(/\s+/)
    .filter((word) => word && !/^(mr|mrs|ms|dr|miss)\.?$/i.test(word));

  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/*
 * Row identity for the master listings: avatar, name, and the employee code
 * underneath. Pairing the two means the code is read as an attribute of the
 * person rather than as its own unrelated column.
 */
const EmployeeIdentity = ({ record, name, code, img, seed, to, state }) => {
  // Listings disagree on where the employee sits on a row — sometimes the row
  // IS the employee, sometimes it hangs off `employees`/`employee_id`. Explicit
  // name/code/img props win; otherwise fall back to the common shapes so a
  // caller can just hand over the record.
  // `employee_id` is often just an ObjectId string rather than a populated doc —
  // only treat a nested value as the employee when it is actually an object,
  // otherwise the row's own fields would be thrown away.
  const nested = [record?.employees, record?.employee_id].find(
    (value) => value && typeof value === "object"
  );
  const source = nested || record || {};
  const fullName =
    name ||
    source?.full_name ||
    [source?.first_name, source?.last_name].filter(Boolean).join(" ") ||
    "-";
  const empCode = code || source?.emp_code;
  const imgName = img || source?.emp_img;
  const tone = toneFor(seed || source?._id || record?._id || fullName);
  const image = imgName
    ? `${Service.Server_Base_URL}/uploads/emp_images/${imgName}`
    : null;

  const nameNode = (
    <Tooltip title={fullName}>
      <span className="ema-identity-name">{fullName}</span>
    </Tooltip>
  );

  return (
    <div className="ema-identity">
      <span
        className="ema-identity-avatar"
        style={{ backgroundColor: tone.bg, color: tone.fg }}
        aria-hidden="true"
      >
        {image ? (
          <img
            src={image}
            alt=""
            // A missing upload must not leave a broken-image glyph in the row —
            // drop the <img> and let the initials underneath show through.
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        ) : null}
        <span className="ema-identity-initials">{initialsFor(fullName)}</span>
      </span>
      <span className="ema-identity-text">
        {to ? (
          <Link className="ema-identity-link" to={state ? { pathname: to, state } : to}>
            {nameNode}
          </Link>
        ) : (
          nameNode
        )}
        {/* Some listings (tickets, approvals) carry only a name — a lone "-"
            under every row would be noise, so the code line is dropped
            entirely rather than rendered empty. */}
        {empCode ? <span className="ema-identity-code">{empCode}</span> : null}
      </span>
    </div>
  );
};

export default EmployeeIdentity;
